import { useEffect, useState } from "react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Section, SectionHeader } from "../components/Section";
import { CTASection } from "../components/CTASection";
import { useTranslation } from "../lib/i18n";
import { useIdentity } from "../lib/identity";
import { createCheckoutSession } from "../lib/stripe-api";
import { fetchPrograms } from "../lib/sentinel-api";
import type { CertificationProgram } from "../lib/certifications";

const LICENSE_PLANS = [
  {
    id: "agency-5",
    name: "Agency Plan — 5 Seats",
    seats: 5,
    price: 2475,
    priceId: "price_1U03tMD2TH4f4lLQH0U6Y7qD",
    type: "agency" as const,
    description: "Perfect for small teams and field offices",
    features: ["5 seats across all levels", "All 4 certification levels", "Annual renewal included", "Seat management dashboard", "Progress tracking"],
  },
  {
    id: "agency-10",
    name: "Agency Plan — 10 Seats",
    seats: 10,
    price: 4455,
    priceId: "price_1U03tMD2TH4f4lLQA3lawxI8",
    type: "agency" as const,
    description: "Great for growing departments",
    features: ["10 seats across all levels", "All 4 certification levels", "Annual renewal included", "Seat management dashboard", "Progress tracking", "10% savings vs individual"],
    popular: true,
  },
  {
    id: "agency-25",
    name: "Agency Plan — 25 Seats",
    seats: 25,
    price: 8950,
    priceId: "price_1U03tMD2TH4f4lLQxhGGPV5g",
    type: "agency" as const,
    description: "For mid-size agencies and precincts",
    features: ["25 seats across all levels", "All 4 certification levels", "Annual renewal included", "Seat management dashboard", "Progress tracking", "Bulk analytics reporting", "20% savings vs individual"],
  },
  {
    id: "enterprise-50",
    name: "Enterprise Plan — 50 Seats",
    seats: 50,
    price: 24950,
    priceId: "price_1U03tND2TH4f4lLQQtLDyIP8",
    type: "enterprise" as const,
    description: "For large organizations and multi-site deployments",
    features: ["50 seats across all levels", "All 4 certification levels", "Annual renewal included", "Advanced seat management", "Org-wide analytics", "Custom branding", "Priority support", "Dedicated account manager"],
  },
];

const LEVEL_LABELS: Record<string, string> = {
  Professional: "Professional",
  Supervisor: "Supervisor",
  Manager: "Manager",
  Executive: "Executive",
};

export default function PricingPage() {
  const { t } = useTranslation();
  const { identity } = useIdentity();
  const [programs, setPrograms] = useState<CertificationProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<string>("all");

  useEffect(() => {
    fetchPrograms().then((p) => {
      setPrograms(p);
      setLoading(false);
    });
  }, []);

  const industries = Array.from(new Set(programs.map((p) => p.industry))).sort();
  const filteredPrograms = selectedIndustry === "all" ? programs : programs.filter((p) => p.industry === selectedIndustry);

  async function handleIndividualCheckout(program: CertificationProgram) {
    if (!identity.isAuthenticated) {
      window.location.href = "/login?redirect=pricing";
      return;
    }
    if (!identity.userId) {
      setError("Unable to identify your account. Please log in again.");
      return;
    }
    setCheckoutLoading(program.id);
    setError(null);
    const session = await createCheckoutSession({
      user_id: identity.userId,
      program_id: program.id,
      license_type: "individual",
      price_plan_id: program.price_plan_id,
      success_url: `${window.location.origin}/dashboard?checkout=success&program=${program.id}`,
      cancel_url: `${window.location.origin}/pricing?checkout=cancelled`,
    });
    setCheckoutLoading(null);
    if (session?.url) {
      window.location.href = session.url;
    } else {
      setError("Unable to start checkout. Please try again or contact support.");
    }
  }

  async function handleLicenseCheckout(planId: string, priceId: string, licenseType: "agency" | "enterprise") {
    if (!identity.isAuthenticated) {
      window.location.href = "/login?redirect=pricing";
      return;
    }
    if (!identity.userId) {
      setError("Unable to identify your account. Please log in again.");
      return;
    }
    setCheckoutLoading(planId);
    setError(null);
    const session = await createCheckoutSession({
      user_id: identity.userId,
      license_type: licenseType,
      price_plan_id: priceId,
      success_url: `${window.location.origin}/dashboard?checkout=success&plan=${planId}`,
      cancel_url: `${window.location.origin}/pricing?checkout=cancelled`,
    });
    setCheckoutLoading(null);
    if (session?.url) {
      window.location.href = session.url;
    } else {
      setError("Unable to start checkout. Please try again or contact support.");
    }
  }

  return (
    <>
      <SEO
        title="Pricing — Sentinel"
        description="Sentinel certification pricing for individuals, agencies, and enterprises. Purchase individual certifications or multi-seat license plans."
        canonical="/pricing"
      />
      <PageHero
        eyebrow={t("pricing.page.eyebrow")}
        title={t("pricing.page.title")}
        description="Purchase individual certifications or multi-seat license plans. All prices include 1-year access to modules, lessons, quizzes, exams, and capstone simulations."
        bgVariant="amber"
        bgImage="https://images.pexels.com/photos/6172540/pexels-photo-6172540.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />

      {error && (
        <div className="container" style={{ maxWidth: "1200px", marginTop: "1rem" }}>
          <div className="card" style={{ padding: "1rem 1.5rem", borderColor: "var(--error-400)", background: "var(--error-50)", color: "var(--error-700)", fontWeight: 600, fontSize: "0.875rem" }}>
            {error}
          </div>
        </div>
      )}

      {/* Individual Certifications */}
      <Section>
        <SectionHeader
          eyebrow="Individual Certifications"
          title="Certify at Your Level"
          description="Purchase individual access to any Sentinel certification program. Each purchase includes 1-year access to all modules, quizzes, exams, and the capstone simulation."
        />

        {/* Industry filter */}
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem", flexWrap: "wrap", justifyContent: "center" }}>
          <button
            onClick={() => setSelectedIndustry("all")}
            style={{
              padding: "0.375rem 0.875rem",
              borderRadius: "var(--radius-full)",
              fontSize: "0.8125rem",
              fontWeight: 600,
              border: selectedIndustry === "all" ? "1px solid var(--accent-500)" : "1px solid var(--border)",
              background: selectedIndustry === "all" ? "var(--accent-50)" : "var(--surface)",
              color: selectedIndustry === "all" ? "var(--accent-700)" : "var(--text-muted)",
              cursor: "pointer",
            }}
          >
            All Industries
          </button>
          {industries.map((ind) => (
            <button
              key={ind}
              onClick={() => setSelectedIndustry(ind)}
              style={{
                padding: "0.375rem 0.875rem",
                borderRadius: "var(--radius-full)",
                fontSize: "0.8125rem",
                fontWeight: 600,
                border: selectedIndustry === ind ? "1px solid var(--accent-500)" : "1px solid var(--border)",
                background: selectedIndustry === ind ? "var(--accent-50)" : "var(--surface)",
                color: selectedIndustry === ind ? "var(--accent-700)" : "var(--text-muted)",
                cursor: "pointer",
              }}
            >
              {ind}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-muted">Loading programs...</p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1rem" }}>
            {filteredPrograms.map((program) => (
              <div key={program.id} className="card card-hover" style={{ padding: "1.25rem", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                  <div>
                    <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)", marginBottom: "0.25rem" }}>
                      {program.industry}
                    </div>
                    <h3 style={{ fontSize: "1rem", marginBottom: "0.25rem" }}>{LEVEL_LABELS[program.level] ?? program.level}</h3>
                  </div>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.6875rem", color: "var(--text-muted)", padding: "0.125rem 0.5rem", borderRadius: "var(--radius-sm)", background: "var(--bg-subtle)" }}>
                    {program.code}
                  </span>
                </div>
                <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "0.75rem", flex: 1 }}>
                  {program.description}
                </p>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                  <div style={{ fontSize: "1.5rem", fontWeight: 800 }}>
                    ${program.price}
                    <span style={{ fontSize: "0.75rem", fontWeight: 400, color: "var(--text-muted)" }}> / year</span>
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                    Renewal: ${program.renewal_price}
                  </div>
                </div>
                <button
                  onClick={() => handleIndividualCheckout(program)}
                  disabled={checkoutLoading === program.id}
                  className="btn btn-primary"
                  style={{ width: "100%", fontSize: "0.8125rem" }}
                >
                  {checkoutLoading === program.id ? "Redirecting..." : identity.isAuthenticated ? "Purchase & Enroll" : "Log In to Purchase"}
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Group / Enterprise Plans */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow="Multi-Seat License Plans"
          title="Team & Enterprise Pricing"
          description="Purchase seats in bulk and assign them to team members. All plans include access to all 4 certification levels across all industries."
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1rem" }}>
          {LICENSE_PLANS.map((plan) => (
            <div
              key={plan.id}
              className="card card-hover"
              style={{
                padding: "1.5rem",
                display: "flex",
                flexDirection: "column",
                borderColor: plan.popular ? "var(--accent-400)" : "var(--border)",
                position: "relative",
              }}
            >
              {plan.popular && (
                <span
                  className="badge badge-accent"
                  style={{ position: "absolute", top: "0.75rem", right: "0.75rem" }}
                >
                  Most Popular
                </span>
              )}
              <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--accent-600)", marginBottom: "0.5rem" }}>
                {plan.type === "enterprise" ? "Enterprise" : "Agency"}
              </div>
              <h3 style={{ fontSize: "1.125rem", marginBottom: "0.5rem" }}>{plan.name}</h3>
              <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "0.75rem" }}>{plan.description}</p>
              <div style={{ fontSize: "1.75rem", fontWeight: 800, marginBottom: "0.75rem" }}>
                ${plan.price.toLocaleString()}
                <span style={{ fontSize: "0.75rem", fontWeight: 400, color: "var(--text-muted)" }}> / year</span>
              </div>
              <ul style={{ display: "flex", flexDirection: "column", gap: "0.375rem", marginBottom: "1rem", flex: 1 }}>
                {plan.features.map((f) => (
                  <li key={f} style={{ display: "flex", gap: "0.5rem", fontSize: "0.8125rem" }}>
                    <span style={{ color: "var(--accent-500)", flexShrink: 0 }}>&#10003;</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleLicenseCheckout(plan.id, plan.priceId, plan.type)}
                disabled={checkoutLoading === plan.id}
                className={plan.popular ? "btn btn-primary" : "btn btn-secondary"}
                style={{ width: "100%", fontSize: "0.8125rem" }}
              >
                {checkoutLoading === plan.id ? "Redirecting..." : identity.isAuthenticated ? `Purchase ${plan.name}` : "Log In to Purchase"}
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* Renewal explanation */}
      <Section>
        <SectionHeader
          eyebrow="Renewal Policy"
          title="How Certification Renewals Work"
          description="All Sentinel certifications require annual renewal to keep your credentials active and ensure you stay current with evolving safety standards."
        />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
          {[
            { level: "Professional", price: 75, hours: 8, requirements: "8 hours of continuing education modules + recertification quiz" },
            { level: "Supervisor", price: 115, hours: 12, requirements: "12 hours of continuing education + recertification quiz + 2 supervised intervention exercises" },
            { level: "Manager", price: 165, hours: 16, requirements: "16 hours of continuing education + recertification exam + organizational safety review" },
            { level: "Executive", price: 215, hours: 20, requirements: "20 hours of continuing education + recertification exam + strategic governance review" },
          ].map((r) => (
            <div key={r.level} className="card" style={{ padding: "1.25rem", borderLeft: "3px solid var(--accent-400)" }}>
              <h4 style={{ fontSize: "0.9375rem", marginBottom: "0.5rem" }}>{r.level} Renewal</h4>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.5rem" }}>
                <span style={{ fontSize: "1.25rem", fontWeight: 800, color: "var(--accent-600)" }}>${r.price}</span>
                <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{r.hours} CE hours</span>
              </div>
              <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>{r.requirements}</p>
            </div>
          ))}
        </div>

        <div className="card" style={{ padding: "1.5rem", background: "var(--accent-50)", borderColor: "var(--accent-200)" }}>
          <h4 style={{ fontSize: "0.9375rem", marginBottom: "0.75rem" }}>Renewal FAQ</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1rem" }}>
            <div>
              <div style={{ fontSize: "0.8125rem", fontWeight: 600, marginBottom: "0.25rem" }}>When do renewals start?</div>
              <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>Your annual renewal begins 1 year from your certification date. You'll receive email reminders 30 days, 15 days, and 1 day before expiration.</p>
            </div>
            <div>
              <div style={{ fontSize: "0.8125rem", fontWeight: 600, marginBottom: "0.25rem" }}>What if my certification lapses?</div>
              <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>You have a 90-day grace period to reinstate by paying the renewal fee plus a reinstatement charge. After 90 days, you must retake the full certification program.</p>
            </div>
            <div>
              <div style={{ fontSize: "0.8125rem", fontWeight: 600, marginBottom: "0.25rem" }}>Do multi-seat plans include renewals?</div>
              <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>Yes. All agency and enterprise plans include annual renewal for every seat. Renewals are automatic as long as your plan is active.</p>
            </div>
            <div>
              <div style={{ fontSize: "0.8125rem", fontWeight: 600, marginBottom: "0.25rem" }}>Can I renew at a different level?</div>
              <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>Yes. You can upgrade to a higher certification level during renewal by paying the price difference plus the renewal fee for the new level.</p>
            </div>
          </div>
        </div>
      </Section>

      <CTASection
        title={t("pricing.cta.title")}
        description="Need a custom plan for 100+ seats or multi-jurisdiction deployments? Contact our team for a tailored quote."
        primaryLabel={t("cta.contactSales")}
        primaryTo="/contact"
        secondaryLabel={t("pricing.cta.secondary")}
        secondaryTo="/licensing"
      />
    </>
  );
}
